import { useEffect } from 'react'
import { useRouterState } from '@tanstack/react-router'

import { navigationItemForLeaf } from '@/app/routes/area-layout'
import { isRecord } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { isUuid } from '@/lib/entity'
import { useLocalStorageState } from '@/lib/use-local-storage-state'
import { useSelectedEntityId } from '@/lib/use-selected-entity'

export type RecentPage = { to: string; entityId: string; visitedAt: string }

const RECENT_PAGES_KEY_PREFIX = 'vantage.recent-pages'
const RECENT_PAGES_LIMIT = 30
const DASHBOARD_PATH = '/$entityId/dashboard'

function isRecentPage(value: unknown): value is RecentPage {
  return (
    isRecord(value) &&
    typeof value.to === 'string' &&
    typeof value.entityId === 'string' &&
    isUuid(value.entityId) &&
    typeof value.visitedAt === 'string' &&
    !Number.isNaN(Date.parse(value.visitedAt))
  )
}

function parseRecentPages(raw: string): RecentPage[] | undefined {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return undefined
  }
  return Array.isArray(parsed) ? parsed.filter(isRecentPage) : undefined
}

function useRecentPagesStore() {
  const userId = useAuth().user?.userId
  return useLocalStorageState<RecentPage[]>(
    userId ? `${RECENT_PAGES_KEY_PREFIX}:${userId}` : null,
    parseRecentPages,
    JSON.stringify,
    [],
  )
}

export function useRecentPages(): RecentPage[] {
  return useRecentPagesStore()[0]
}

export function useRecordPageVisit(): void {
  const [, setPages] = useRecentPagesStore()
  const entityId = useSelectedEntityId()
  const leaf = useRouterState({ select: (state) => state.matches.at(-1)?.fullPath })

  useEffect(() => {
    const to = navigationItemForLeaf(leaf)?.to
    if (!entityId || !to || to === DASHBOARD_PATH) return
    const visit = { to, entityId, visitedAt: new Date().toISOString() }
    setPages((prev) =>
      [visit, ...prev.filter((page) => page.to !== to || page.entityId !== entityId)].slice(
        0,
        RECENT_PAGES_LIMIT,
      ),
    )
  }, [leaf, entityId, setPages])
}
